import type { Contact, ContactEvent } from "@/features/contact/types";

import { groupBy } from "lodash-es";
import { v7 } from "uuid";

import {
  EventByTypeGroup,
  EventsByDayGroup,
} from "@/features/inbox/components/conversation/conversation-body/body/group";
import {
  ACTIVITY_TYPE,
  type Activity,
  type ActivityType,
} from "@/types/activity";
import {
  Attachment,
  ATTACHMENT_SOURCE,
  ATTACHMENT_STATUS,
  AttachmentType,
  Message,
  MESSAGE_STATUS,
  MESSAGE_TYPE,
  MessageStatus,
  MessageRunFlags,
  MessageStatusRecord,
  OWNER,
  SEND_EVENT_MESSAGE_TYPE,
  SendCommentPayload,
  SendMessagePayload,
} from "@/types/message";
import { User } from "@/types/user";
import { aiWroteMessage } from "@/utils/ai-authorship";

const INBOX_VISIBLE_ACTIVITY_TYPES = new Set<ActivityType>([
  ACTIVITY_TYPE.CREATED,
  ACTIVITY_TYPE.NOTE,
  ACTIVITY_TYPE.ASSIGNED,
  ACTIVITY_TYPE.UNASSIGNED,
  ACTIVITY_TYPE.CONVERSATION_STATUS_CHANGE,
  ACTIVITY_TYPE.LIFECYCLE_CHANGE,
  ACTIVITY_TYPE.APPOINTMENT_CREATED,
  ACTIVITY_TYPE.APPOINTMENT_UPDATED,
  ACTIVITY_TYPE.APPOINTMENT_RESCHEDULED,
  ACTIVITY_TYPE.APPOINTMENT_DELETED,
  ACTIVITY_TYPE.APPOINTMENT_OUTCOME,
  ACTIVITY_TYPE.APPOINTMENT_BILLED,
  ACTIVITY_TYPE.ORDER_UPDATE,
  ACTIVITY_TYPE.CALL,
  ACTIVITY_TYPE.BLOCK,
  ACTIVITY_TYPE.UNBLOCK,
  ACTIVITY_TYPE.OPT_OUT,
  ACTIVITY_TYPE.OPT_IN,
  ACTIVITY_TYPE.ADS_CLICKED,
  ACTIVITY_TYPE.SNOOZE,
  ACTIVITY_TYPE.UNSNOOZE,
  ACTIVITY_TYPE.TAG_ADDED,
  ACTIVITY_TYPE.TAG_REMOVED,
  ACTIVITY_TYPE.WEBSITE_VISITED,
  ACTIVITY_TYPE.FORM_SUBMITTED,
  ACTIVITY_TYPE.AI_PAUSED,
  ACTIVITY_TYPE.AI_RESUMED,
  ACTIVITY_TYPE.AI_SESSION,
]);

export const makeEventFromSendMessage = (
  message: SendMessagePayload,
  user: User,
  ConversationLinkedMessage?: Message | null,
): Message => {
  let attachments: Attachment[] = [];
  const isQuickReply = message.type === SEND_EVENT_MESSAGE_TYPE.QUICK_REPLY;

  if (
    message.type !== SEND_EVENT_MESSAGE_TYPE.TEXT &&
    message.type !== SEND_EVENT_MESSAGE_TYPE.QUICK_REPLY
  ) {
    attachments.push({
      ...(message[message.type] as any),
      type: message.type,
      updatedAt: new Date(),
      createdAt: new Date(),
      externalId: "",
      source: ATTACHMENT_SOURCE.CONVERSATION,
      status: ATTACHMENT_STATUS.READY,
    });
  }

  return {
    attachments,
    type: isQuickReply
      ? MESSAGE_TYPE.QUICK_REPLY
      : attachments.length > 0
        ? MESSAGE_TYPE.ATTACHMENT
        : MESSAGE_TYPE.TEXT,
    ...(isQuickReply && { payload: message.quickReply }),
    isAiAssisted: Boolean(message.isAiAssisted),
    text: message.text!,
    createdAt: new Date().toISOString(),
    externalId: "",
    id: v7(),
    organizationId: user.organizationId,
    channel: { id: message.channelId },
    owner: OWNER.USER,
    reactions: [],
    linked: ConversationLinkedMessage ?? null,
    statuses: [
      {
        messageId: v7(),
        id: v7(),
        createdAt: new Date(),
        receivedAt: null,
        status: MESSAGE_STATUS.SENDING,
        updatedAt: new Date(),
      },
    ],
    updatedAt: new Date().toISOString(),
    user: user,
    error: null,
    receivedAt: null,
  };
};

/** One outgoing bubble of an AI-drafted reply, in the agent-step shape. */
export interface OptimisticReplyBubble {
  type: string;
  text: string;
  mediaUrl?: string;
}

/**
 * Optimistic conversation bubble for a drafted AI reply the operator just sent
 * from the timeline card. Mirrors `makeEventFromSendMessage` but marks the
 * message AI-owned (matching how the backend delivers approved replies) so it
 * doesn't visibly flip appearance when the delivered message lands and
 * reconciles onto it. `isAiAssisted` must match what the backend will store -
 * true only for a draft sent as written - or an edited reply shows the AI badge
 * until the real message arrives. `id` MUST be the same id handed to the
 * send-response endpoint so the pushed message dedupes onto this row by id.
 */
export const makeOptimisticReplyMessage = (
  bubble: OptimisticReplyBubble,
  id: string,
  context: {
    contactId: string;
    channelId: string;
    user: User;
    isAiAssisted: boolean;
  },
): Message => {
  const hasMedia = Boolean(bubble.mediaUrl);
  const attachments: Attachment[] = hasMedia
    ? [
        {
          id: v7(),
          url: bubble.mediaUrl!,
          name: null,
          caption: bubble.text || null,
          externalId: "",
          type: bubble.type as AttachmentType,
          createdAt: new Date(),
          updatedAt: new Date(),
          contentType: "",
          source: ATTACHMENT_SOURCE.CONVERSATION,
          status: ATTACHMENT_STATUS.READY,
        },
      ]
    : [];

  return {
    id,
    text: bubble.text || null,
    owner: OWNER.AI,
    isAiAssisted: context.isAiAssisted,
    externalId: "",
    organizationId: context.user.organizationId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    receivedAt: null,
    statuses: [
      {
        messageId: v7(),
        id: v7(),
        createdAt: new Date(),
        receivedAt: null,
        status: MESSAGE_STATUS.SENDING,
        updatedAt: new Date(),
      },
    ],
    attachments,
    reactions: [],
    user: context.user,
    type: hasMedia ? MESSAGE_TYPE.ATTACHMENT : MESSAGE_TYPE.TEXT,
    channel: { id: context.channelId },
    linked: null,
    error: null,
  };
};

export const makeEventFromSendComment = (
  message: SendCommentPayload,
  user: User,
  ConversationLinkedMessage?: Message | null,
): Message => {
  let attachments: Attachment[] = [];

  return {
    attachments,
    type: MESSAGE_TYPE.COMMENT,
    text: message.text!,
    createdAt: new Date().toISOString(),
    externalId: "",
    id: v7(),
    organizationId: user.organizationId,
    channel: { id: message.channelId },
    owner: OWNER.USER,
    reactions: [],
    linked: ConversationLinkedMessage ?? null,
    statuses: [
      {
        messageId: v7(),
        id: v7(),
        createdAt: new Date(),
        receivedAt: null,
        status: MESSAGE_STATUS.SENDING,
        updatedAt: new Date(),
      },
    ],
    updatedAt: new Date().toISOString(),
    user: user,
    error: null,
    receivedAt: null,
  };
};

export const hasEventPayload = (event: ContactEvent): boolean =>
  event.type === "message" ? !!event.message : !!event.activity;

export function groupEventsByDayAndOwner(
  events: ContactEvent[],
  contact: Contact,
): EventsByDayGroup[] {
  const _events = events
    .filter(
      (event) =>
        hasEventPayload(event) &&
        (event.type !== "activity" ||
          INBOX_VISIBLE_ACTIVITY_TYPES.has(event.activity.type)),
    )
    .sort(
      (eventA, eventB) =>
        new Date(eventA.createdAt).getTime() -
        new Date(eventB.createdAt).getTime(),
    );
  const groupedByDay = groupBy(_events, (e) => {
    try {
      return new Date(e.createdAt)?.toISOString().split("T")[0];
    } catch (error) {
      console.log("event with error", e, e.createdAt);
      throw error;
    }
  });

  const result: EventsByDayGroup[] = Object.entries(groupedByDay)
    .sort(
      ([dateA], [dateB]) =>
        new Date(dateA).getTime() - new Date(dateB).getTime(),
    )
    .map(([date, dailyEvents]) => {
      const eventGroups: EventByTypeGroup[] = [];
      let currentMessageGroup: (EventByTypeGroup & { type: "message" }) | null =
        null;
      let currentActivityGroup:
        | (EventByTypeGroup & { type: "activity" })
        | null = null;

      for (const event of dailyEvents) {
        if (event.type === "activity") {
          currentMessageGroup = null;
          if (currentActivityGroup) {
            currentActivityGroup.activities.push(event.activity);
          } else {
            currentActivityGroup = {
              type: "activity",
              activities: [event.activity],
            };
            eventGroups.push(currentActivityGroup);
          }
        } else if (event.type === "message") {
          currentActivityGroup = null;
          const message = event.message;

          const isAgent = message.owner === OWNER.USER;
          const isUser = message.owner === OWNER.CONTACT;
          // Show both the human + AI avatars when a human sent something the AI
          // wrote: a composer draft/rewrite, or a draft approved as written.
          // Owner alone doesn't say - a draft the reviewer rewrote also goes out
          // owner `ai` - so this asks who wrote the words.
          const isAiAssisted = Boolean(message.user) && aiWroteMessage(message);
          // Assisted messages keep the human agent on the group so its avatar
          // renders alongside the AI chip.
          const carriesUser = isAgent || isAiAssisted;
          const sameOwner =
            currentMessageGroup &&
            currentMessageGroup.owner === message.owner &&
            Boolean(currentMessageGroup.isAiAssisted) === isAiAssisted &&
            (!carriesUser || currentMessageGroup.user?.id === message.user?.id);
          const sameChannel =
            currentMessageGroup &&
            currentMessageGroup.channel?.id === message.channel?.id;
          // Two AI replies back to back (a reply, then a follow-up chase) are
          // separate runs and each gets its own review control.
          const sameGeneration =
            message.owner !== OWNER.AI ||
            !currentMessageGroup ||
            currentMessageGroup.messages[0]?.meta?.generationId ===
              message.meta?.generationId;
          const sameGroup = sameOwner && sameChannel && sameGeneration;

          if (sameGroup && currentMessageGroup) {
            currentMessageGroup.messages.push(message);
          } else {
            currentMessageGroup = {
              type: "message",
              messages: [message],
              contact: isUser ? contact : null,
              user: carriesUser ? message.user : null,
              owner: message.owner,
              isAiAssisted,
              channel: { id: message.channel.id },
            };
            eventGroups.push(currentMessageGroup);
          }
        }
      }

      return {
        date,
        eventGroups,
      };
    });

  return result;
}

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export const sortEvents = (events: Array<ContactEvent>) => {
  return events.sort(
    (eventA, eventB) =>
      new Date(eventA.createdAt).getTime() -
      new Date(eventB.createdAt).getTime(),
  );
};

// Delivery is an append-only log, so a message carries every state it has
// passed through. Ranking picks the furthest one reached rather than the newest
// row - a late-arriving `delivered` DLR must not walk a message back from read.
const STATUS_PRIORITY: Record<MessageStatus, number> = {
  [MESSAGE_STATUS.FAILED]: 9,
  [MESSAGE_STATUS.READ]: 8,
  [MESSAGE_STATUS.DELIVERED]: 7,
  [MESSAGE_STATUS.SENT]: 6,
  // Outranks accepted: a held message is stuck pending WhatsApp's quality
  // review, which is worth surfacing over the generic hand-off state.
  [MESSAGE_STATUS.HELD_FOR_QUALITY_ASSESSMENT]: 5,
  [MESSAGE_STATUS.ACCEPTED]: 4,
  [MESSAGE_STATUS.PENDING]: 3,
  [MESSAGE_STATUS.RECEIVED]: 2,
  [MESSAGE_STATUS.SENDING]: 1,
};

export const findActivityInEvents = (
  events: ContactEvent[] | undefined,
  activityId: string,
): Activity | undefined => {
  if (!events) return undefined;
  for (const event of events) {
    if (event.type === "activity" && event.activity.id === activityId) {
      return event.activity;
    }
  }

  return undefined;
};

export const replaceActivityInEvents = (
  events: ContactEvent[] | undefined,
  activity: Activity,
): ContactEvent[] | undefined => {
  if (!events) return events;

  return events.map((event) => {
    if (event.type === "activity" && event.activity.id === activity.id) {
      return { ...event, activity };
    }

    return event;
  });
};

export const applyRunFlagsToEvents = (
  events: ContactEvent[] | undefined,
  messageId: string,
  flags: MessageRunFlags,
): ContactEvent[] | undefined => {
  const index =
    events?.findIndex(
      (event) => event.type === "message" && event.message.id === messageId,
    ) ?? -1;
  const target = index < 0 ? undefined : events?.[index];

  if (!events || target?.type !== "message") return events;

  const patched = [...events];

  patched[index] = { ...target, message: { ...target.message, ...flags } };

  return patched;
};

export const sortStatus = (statuses: Array<MessageStatusRecord>) => {
  return [...statuses].sort((statusA, statusB) => {
    const priorityA = STATUS_PRIORITY[statusA.status] ?? 0;
    const priorityB = STATUS_PRIORITY[statusB.status] ?? 0;

    return priorityB - priorityA;
  });
};
